import { useEffect, useState } from "react";

// Interfaces/Types
import { ICommissionCycle, IPayPeriod, IStatus } from "@/Common/api/types/CommissionCycle";
import capitalize from "lodash-es/capitalize";

// Context
import { usePayrollClosing } from "@/Washouts/context/payrollClosing";

// Helpers
import { getPeriodStatusesCombined } from "@/Washouts/helpers/Statuses";

// MUI
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

// Components
import { IPayrollClosingSideBarSelected } from "@/Washouts/types/PayrollClosing/PayrollClosingSideBar";
import ApproveAllWashoutButton from "../../ApproveAllWashoutButton";
import { ChangeRequestContainer } from "../../ChangeRequestContainer";
import CommissionCycleClosingReviewers from "../CommissionCycleClosingReviewers";
import CommissionCycleClosingApprovalsActions from "./CommissionCycleClosingApprovalsActions";
import { useSelectionPayrollClosing } from "@/Washouts/context/selectionPayrollClosingContext";

type CommissionCycleClosingSectionProps = {
	selectedItem: IPayrollClosingSideBarSelected | null;
	locationId: string;
	commissionCyclesLoading?: boolean;
	commissionCycle?: ICommissionCycle;
	payPeriod?: IPayPeriod;
	partnerPayPeriod?: IPayPeriod | null;
	positionStatusPayPeriodParts?: IStatus[];
};

const isClosedStatus = (status = "") => ["POSTED", "FINALIZED"].includes(status);

const hasNoPositionsInReview = (positionStatusArray: IStatus[]) => {
	return !positionStatusArray.some((statusObj) => statusObj.status === "REVIEWING");
};

const sortedPeriods = (payPeriods: IPayPeriod[]) => {
	return [...payPeriods].sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
};

export default function CommissionCycleClosingSection(props: Readonly<CommissionCycleClosingSectionProps>) {
	const {
		selectedItem,
		locationId,
		commissionCyclesLoading,
		commissionCycle,
		payPeriod,
		partnerPayPeriod,
		positionStatusPayPeriodParts,
	} = props;

	const [closeCommissionButtonVisible, setCloseCommissionButtonVisible] = useState(true);

	const { setHeaderTitle } = usePayrollClosing();
	const { setSelectedCommissionCycle, selectedCommissionCycle } = useSelectionPayrollClosing();

	useEffect(() => {
		if (commissionCycle && payPeriod) {
			const payPeriodsOrdered = sortedPeriods(commissionCycle.payPeriods);
			const lastPayPeriod = payPeriodsOrdered.at(-1);
			const isLastPayPeriod = selectedItem?.payPeriodId === lastPayPeriod?._id && selectedItem?.cycleId === commissionCycle._id;
			setCloseCommissionButtonVisible(isLastPayPeriod || !!payPeriod?.previousCCId);
			setHeaderTitle(
				isLastPayPeriod
					? `Commission Cycle (${capitalize(commissionCycle.status)})`
					: `Pay Period Status (${capitalize(payPeriod.status)})`
			);
			setSelectedCommissionCycle(commissionCycle);
		}
		return () => {
			window.history.replaceState({}, document.title);
			setSelectedCommissionCycle(undefined as unknown as ICommissionCycle);
		};
	}, [commissionCycle, payPeriod, selectedCommissionCycle]);

	// Find the pay period that matches the selected pay period ID
	const selectedPayPeriod = commissionCycle?.payPeriods.find((period) => period._id === selectedItem?.payPeriodId);

	// Extract the positionStatus array from the selected pay period
	const positionStatusArray = selectedPayPeriod?.positionStatus || [];

	const isDisabledApprove = isClosedStatus(commissionCycle?.status) || hasNoPositionsInReview(positionStatusArray);

	return (
		<>
			{!commissionCyclesLoading && (
				<Grid container>
					<CommissionCycleClosingApprovalsActions
						commissionCycleId={commissionCycle?._id ?? ""}
						payPeriodId={payPeriod?._id ?? ""}
						closeCommissionButtonVisible={closeCommissionButtonVisible}
						partnerPayPeriod={partnerPayPeriod}
					/>
					<Grid item xs={12} className="pt-[40px] pl-[20px]">
						<Grid container className="flex-row" spacing={5.5}>
							<CommissionCycleClosingReviewers
								locationId={locationId}
								commissionCycleId={commissionCycle?._id ?? ""}
								payPeriodId={payPeriod?._id ?? ""}
								statuses={getPeriodStatusesCombined(payPeriod, partnerPayPeriod)}
							/>
						</Grid>
					</Grid>
					<Grid item xs={12} sx={{ paddingTop: "40px" }}>
						<Grid item>
							<Typography className="page-title" component="div">
								Change Requests
							</Typography>
						</Grid>
					</Grid>
					<Grid item xs={12} sx={{ paddingTop: "20px" }}>
						<ChangeRequestContainer
							locationId={locationId}
							payPeriodId={payPeriod?._id ?? ""}
							hideOpenWashout={false}
							hideEscalateAction={false}
							canEditWashout={true}
							tableProps={{
								switchValue: "PENDING",
							}}
							statusActionTooltipText={"Resolve from Support Center"}
						/>
					</Grid>
					<Grid item xs={12} sx={{ paddingTop: "20px", display: "flex", justifyContent: "flex-end" }}>
						<ApproveAllWashoutButton
							commissionCycleId={commissionCycle?._id}
							payPeriod={payPeriod}
							disabled={isDisabledApprove}
							partnerPayPeriod={partnerPayPeriod}
							positionStatuses={positionStatusPayPeriodParts}
						/>
					</Grid>
				</Grid>
			)}
		</>
	);
}
